// Sends the Klaviyo "Ordered Swatches" event from the order confirmation page and then
// empties the swatch cart. Both belong here: the event carries the swatches that were
// ordered, and those only exist in the local-storage cart until it is cleared.
//
// The order itself comes from the #swatch-order-tracking element that
// Views/OrderConfirmationPage/Index.cshtml renders, and only on a successful order, so a
// refresh or a direct hit sends nothing.

import { trackSwatchOrder } from './klaviyo';
import { CART_STORAGE_KEY, CART_UPDATED_EVENT } from '../swatch-cart-favorites';

const trackingElementId = 'swatch-order-tracking';

const readOrder = () => {
  const element = document.getElementById(trackingElementId);
  if (!element) {
    return null;
  }
  try {
    return JSON.parse(element.textContent);
  } catch (e) {
    return null;
  }
};

const readCart = () => {
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return (parsed && Array.isArray(parsed.items)) ? parsed.items : [];
  } catch (e) {
    return [];
  }
};

const clearCart = () => {
  try {
    window.localStorage.removeItem(CART_STORAGE_KEY);
  } catch (e) {
    // Local storage may be unavailable (private mode / quota); fail silently.
  }
  window.dispatchEvent(new window.Event(CART_UPDATED_EVENT));
};

// Cart items added from a swatch card carry the color name under `colorName`; ones added
// from a tile button fall back to `name`.
const toEventSwatch = item => ({
  swatch_id: item.id,
  color_name: item.colorName || item.name,
  color_number: item.colorNumber,
  collection: item.collection,
  product_line: item.productLineName,
  category: item.swatchCategoryName,
});

// The event payload upserts the profile too, so the customer shows up in Klaviyo for future
// communication whether or not they opted in; `accepts_marketing` is what flows should read
// before actually emailing them about offers and promotions.
const buildProfileAttributes = order => ({
  email: order.email,
  first_name: order.firstName,
  last_name: order.lastName,
  location: {
    address1: order.address1,
    address2: order.address2,
    city: order.city,
    region: order.region,
    zip: order.zip,
    country: order.country,
  },
  properties: {
    accepts_marketing: order.acceptsMarketing,
    requested_design_consultation: order.requestedDesignConsultation,
    source: 'Swatch Order',
  },
});

const buildEventProperties = (order, swatches) => ({
  order_number: order.orderNumber,
  swatch_count: swatches.length,
  ordered_swatches: swatches,
  swatch_names: swatches.map(swatch => swatch.color_name),
  accepts_marketing: order.acceptsMarketing,
  requested_design_consultation: order.requestedDesignConsultation,
  microsite: window.micrositeName,
  page_submitted_from: window.location.href,
});

export default () => {
  const order = readOrder();
  if (!order) {
    return;
  }

  const swatches = readCart().map(toEventSwatch);

  // The request body is built before the cart is cleared, and the fetch uses keepalive, so
  // clearing (and any navigation away) cannot cut the event short.
  trackSwatchOrder(buildProfileAttributes(order), buildEventProperties(order, swatches));

  clearCart();
};
